import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';

interface AtrasoStats {
  tipoAtraso: string;
  cantidad: number;
}

interface ProgressItem {
  label: string;
  value: number;
  percent: string;
  widthPercent: number;
  color: string;
}

@Component({
  selector: 'app-control-dus-atraso-chart',
  standalone: true,
  imports: [MatCardModule, CommonModule, MatButtonModule, MatIconModule, MatMenuModule],
  templateUrl: './control-dus-atraso-chart.component.html',
  styleUrls: ['./control-dus-atraso-chart.component.scss'],
})
export class ControlDusAtrasoChartComponent implements OnChanges {
  @Input() statistics: AtrasoStats[] = [];

  items: ProgressItem[] = [];

  // Paleta de colores para las barras
  private readonly colorPalette = [
    '#3761ee', '#796df6', '#00cae3', '#ffb264', '#2ed47e',
    '#e74c3c', '#26a69a', '#d10ce8', '#ff9800', '#4caf50',
    '#9c27b0', '#607d8b',
  ];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['statistics'] && this.statistics.length > 0) {
      this.processData();
    }
  }

  /**
   * Procesa los datos de estadísticas del backend
   */
  private processData(): void {
    const total = this.statistics.reduce((sum, item) => sum + item.cantidad, 0);
    const maxValue = Math.max(...this.statistics.map(s => s.cantidad));

    this.items = this.statistics.map((item, index) => {
      const label = this.normalizarTexto(item.tipoAtraso);
      const value = item.cantidad;
      const percent = total > 0 ? ((value / total) * 100).toFixed(1) : '0';
      // Width relative to max value (so largest bar is 100%)
      const widthPercent = maxValue > 0 ? (value / maxValue) * 100 : 0;
      const color = this.colorPalette[index % this.colorPalette.length];

      return { label, value, percent, widthPercent, color };
    });
  }

  private normalizarTexto(texto: string): string {
    if (!texto) return 'Sin Información';
    // Capitalize first letter of each word
    return texto
      .toLowerCase()
      .replace(/\?/g, 'ó')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  /**
   * Genera el contenido SVG del gráfico
   */
  private generateSvgContent(): string {
    const width = 600;
    const rowHeight = 40;
    const headerHeight = 60; // Title space
    const padding = 20;
    const height = headerHeight + (this.items.length * rowHeight) + padding;

    let svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" style="font-family: Arial, sans-serif;">`;

    // Background
    svg += `<rect width="100%" height="100%" fill="white"/>`;

    // Title
    svg += `<text x="${padding}" y="35" font-size="20" font-weight="bold" fill="#333">DUS por Tipo de Atraso</text>`;

    this.items.forEach((item, index) => {
      const y = headerHeight + (index * rowHeight);

      // Label
      svg += `<text x="${padding}" y="${y + 20}" font-size="12" fill="#334155">${item.label}</text>`;

      // Value & Percent (Right aligned)
      const valueText = `${item.value}`;
      const percentText = `${item.percent}%`;
      svg += `<text x="${width - padding - 60}" y="${y + 20}" font-size="12" font-weight="bold" fill="#1e293b" text-anchor="end">${valueText}</text>`;
      svg += `<text x="${width - padding}" y="${y + 20}" font-size="11" fill="#64748b" text-anchor="end">${percentText}</text>`;

      // Bar Background
      svg += `<rect x="${padding}" y="${y + 28}" width="${width - (padding * 2)}" height="6" rx="3" fill="#f1f5f9"/>`;

      // Bar Fill
      const maxBarWidth = width - (padding * 2);
      const barWidth = (item.widthPercent / 100) * maxBarWidth;
      svg += `<rect x="${padding}" y="${y + 28}" width="${Math.max(barWidth, 4)}" height="6" rx="3" fill="${item.color}"/>`;
    });

    svg += `</svg>`;
    return svg;
  }

  /**
   * Descarga el gráfico en formato SVG
   */
  downloadSvg(): void {
    const svgContent = this.generateSvgContent();
    const blob = new Blob([svgContent], { type: 'image/svg+xml;charset=utf-8' });
    this.downloadFile(blob, 'svg');
  }

  /**
   * Descarga el gráfico en formato PNG
   */
  downloadPng(): void {
    const svgContent = this.generateSvgContent();
    const image = new Image();
    const svgBlob = new Blob([svgContent], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);

    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 600;
      canvas.height = 60 + (this.items.length * 40) + 20;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(image, 0, 0);
        canvas.toBlob((blob) => {
          if (blob) {
            this.downloadFile(blob, 'png');
          }
          URL.revokeObjectURL(url);
        });
      }
    };

    image.src = url;
  }

  /**
   * Descarga los datos en formato CSV
   */
  downloadCsv(): void {
    if (this.items.length === 0) return;

    const headers = ['Tipo de Atraso', 'Cantidad', 'Porcentaje'];
    const rows = this.items.map(item => [
      `"${item.label}"`,
      item.value,
      `${item.percent}%`
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    this.downloadFile(blob, 'csv');
  }

  private downloadFile(blob: Blob, extension: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `control_dus_atraso_${new Date().toISOString().slice(0, 10)}.${extension}`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}
